import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard } from "lucide-react";
import { BottomNavigation } from "./BottomNavigation";
import CartaArredaDrawer from "@/components/CartaArredaDrawer";
import { useAuth } from "@/contexts/AuthContext";

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const showNav = true;
  // Le pagine Account e Aiuto gestiscono la propria clearance (sfondo bianco fino alla nav).
  const ownsClearance =
    location.pathname === "/profilo" || location.pathname === "/concierge";
  // Floating "Carta Arreda" button — hidden on the card, points, account e aiuto pages
  const showCard =
    location.pathname !== "/volantino" &&
    !ownsClearance &&
    !location.pathname.startsWith("/punti");

  // Il pulsante resta chiuso (icona) finché la pagina non ha finito di caricare,
  // poi si apre. Sulle pagine senza pulsante non viene mai renderizzato.
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    setPageReady(false);
    const t = setTimeout(() => setPageReady(true), 500);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Collapse the card button to an icon when scrolling down, expand when scrolling up.
  const [cardCollapsed, setCardCollapsed] = useState(false);
  useEffect(() => {
    if (!showCard) return;
    let last = window.scrollY;
    setCardCollapsed(false);
    const onScroll = () => {
      const y = window.scrollY;
      if (y > last + 6 && y > 40) setCardCollapsed(true);
      else if (y < last - 6) setCardCollapsed(false);
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [showCard, location.pathname]);

  // Reserve space so content isn't hidden behind the bar (+ card button when shown).
  // La pagina Account gestisce la propria clearance (sfondo bianco fino alla nav),
  // quindi qui non aggiungiamo padding per evitare la fascia grigia doppia.
  // Lo spazio riservato viaggia anche come variabile CSS: le pagine che vogliono
  // riempire lo schermo lo sottraggono dalla propria altezza minima, altrimenti
  // sommano 100dvh + clearance e la pagina scorre anche quando il contenuto ci sta.
  const mainStyle = !ownsClearance
    ? ({
        "--clearance-bottom": `calc(var(--safe-area-bottom, 0px) + ${showCard ? 96 : 74}px)`,
        paddingBottom: "var(--clearance-bottom)",
      } as CSSProperties)
    : undefined;

  // Measure the label so the expanded pill fits the text exactly (no trailing gap).
  // width = pl-14 (14) + icon (20) + ml-2.5 (10) + label + right padding (14)
  const labelRef = useRef<HTMLSpanElement>(null);
  const [expandedW, setExpandedW] = useState(170);
  useEffect(() => {
    if (labelRef.current) setExpandedW(labelRef.current.scrollWidth + 58);
  }, []);

  const [cardOpen, setCardOpen] = useState(false);
  const openCard = () => {
    if (user) setCardOpen(true);
    else navigate("/login", { state: { from: { pathname: "/profilo" } } });
  };

  // Chiuso se stiamo scrollando in giù oppure se la pagina non ha ancora finito di caricare.
  const collapsed = cardCollapsed || !pageReady;

  return (
    <div className="min-h-[100dvh] bg-background">
      {/* Fade-in di pagina: al cambio rotta AppLayout rimonta, quindi initial→animate
          parte a ogni apertura. Alla prima apertura lo skeleton (portal) copre sopra;
          al ritorno, senza skeleton, resta questo fade moderno corto con micro-lift.
          key = pathname per garantire il replay anche se React riusa il nodo. */}
      <motion.main
        key={location.pathname}
        // Lo sfondo sotto la fascia riservata: senza, le pagine che si fermano
        // prima della nav lascerebbero vedere una striscia di colore diverso.
        className={ownsClearance ? undefined : "bg-appBg"}
        style={mainStyle}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.main>

      {showCard && (
        <motion.button
          onClick={openCard}
          aria-label="Apri Carta Arreda"
          initial={{ width: 48 }}
          animate={{ width: collapsed ? 48 : expandedW }}
          transition={{ type: "spring", stiffness: 300, damping: 30, mass: 0.9 }}
          whileTap={{ scale: 0.95 }}
          className="fixed right-4 z-50 flex items-center h-12 pl-[14px] overflow-hidden rounded-full bg-[#111] text-white shadow-[0_10px_28px_-8px_rgba(17,17,17,0.55)]"
          style={{ bottom: "calc(var(--safe-area-bottom, 0px) + 70px)" }}
        >
          <CreditCard className="w-5 h-5 flex-shrink-0" />
          <motion.span
            ref={labelRef}
            animate={{ opacity: collapsed ? 0 : 1, x: collapsed ? -4 : 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="text-[15px] font-semibold whitespace-nowrap ml-2.5"
          >
            Carta Arreda
          </motion.span>
        </motion.button>
      )}

      {showNav && <BottomNavigation />}

      <CartaArredaDrawer open={cardOpen} onOpenChange={setCardOpen} />
    </div>
  );
}
